import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Grid2,
  Paper,
  Typography
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useReferenceData } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { FlowTestForm, getFlowTestFormSchema } from "../../types/forms/flowTest";
import FormAutocomplete from "../General/FormAutocomplete";

interface AddFlowTestFormProps {
  onSubmit: (values: FlowTestForm) => Promise<void>;
}

export default function AddFlowTestForm({
  onSubmit
}: Readonly<AddFlowTestFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();
  const formStyles = useFormStyles(true);

  const validationSchema = getFlowTestFormSchema(t);

  const methods = useForm<FlowTestForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      testTypeId: null,
      startDate: undefined,
      aquiferId: null,
    }
  });

  const { data: flowTestTypes = [] } = useReferenceData("flow_test", "flow_test_types");
  const { data: aquiferSystems = [] } = useReferenceData("hydro", "aquifer_systems");

  const handleFormSubmit = async (values: FlowTestForm) => {
    try {
      await onSubmit(values);
    } catch (error) {
      handleError(error);
    }
  };



  useEffect(() => {
    methods.reset();
  }, []);

  return (
    <FormProvider {...methods}>
      <Box
        sx={{ p: 2 }}
        component="form"
        onSubmit={methods.handleSubmit(handleFormSubmit)}
      >
        <Grid2 container spacing={2}>
          {/* Test Information */}
          <Grid2 size={{ xs: 12 }}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                borderRadius: 2, 
                flex: 1, 
                display: "flex", 
                flexDirection: "column", 
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t("flow-tests.sections.test-information")}
                </Typography>
                <Grid2 container spacing={2} mt={1}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <FormAutocomplete
                      name="testTypeId"
                      label={t("flow-tests.flow-test.test-type")}
                      options={flowTestTypes}
                      isEditing={true} 
                      isSaving={false} 
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name="startDate"
                        control={methods.control}
                        render={({ field }) => (
                          <DatePicker
                            label={t("flow-tests.flow-test.start-date")}
                            value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                            onChange={(date) => {
                              field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                            }}
                            format="DD-MM-YYYY"
                            sx={{ ...formStyles }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: "outlined",
                                placeholder: "DD-MM-YYYY",
                                error: !!methods.formState.errors.startDate,
                                helperText: methods.formState.errors.startDate?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <FormAutocomplete
                      name="aquiferId"
                      label={t("flow-tests.flow-test.aquifer")}
                      options={aquiferSystems}
                      isEditing={true} 
                      isSaving={false} 
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
        {/* Action Bar */}
        <Grid2 size={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3}}>          
          <Box sx={{display: "flex", gap: 2}}>
            <Button type="submit" variant="contained">
              {t("generic.create")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(-1)}
            >
              {t("generic.cancel")}
            </Button>
          </Box>
        </Grid2>
      </Box>
    </FormProvider>
  );
}